import { useEffect, useMemo, useState } from "react";
import DealerListingPhotoManager, {
  listingPhotosToEntries,
} from "./DealerListingPhotoManager";
import { updateDealerListing } from "../../services/automapDealerInventoryApi";
import { trackInventoryEvent } from "../../constants/automapDealerInventoryConstants";

export default function DealerListingPhotoEditor({
  dealerId,
  listingId,
  initialPhotos = [],
  disabled = false,
  labels,
  onSaved,
  onError,
}) {
  const [photos, setPhotos] = useState(() => listingPhotosToEntries(initialPhotos));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPhotos(listingPhotosToEntries(initialPhotos));
    setDirty(false);
  }, [listingId, initialPhotos]);

  const photoLabels = useMemo(
    () => ({
      helper: labels.helper,
      empty: labels.empty,
      primaryBadge: labels.primaryBadge,
      addUrl: labels.addUrl,
      orAddUrl: labels.orAddUrl,
      addUrlPlaceholder: labels.addUrlPlaceholder,
      uploadHint: labels.uploadHint,
      uploadFiles: labels.uploadFiles,
      uploadingFiles: labels.uploadingFiles,
      uploadedFileNote: labels.uploadedFileNote,
      uploadedFilePlaceholder: labels.uploadedFilePlaceholder,
      moveUp: labels.moveUp,
      moveDown: labels.moveDown,
      setPrimary: labels.setPrimary,
      remove: labels.remove,
      count: labels.count,
      invalidUrl: labels.invalidUrl,
      tooMany: labels.tooMany,
      tooLarge: labels.tooLarge,
      invalidType: labels.invalidType,
      emptyFile: labels.emptyFile,
      uploadFailed: labels.uploadFailed,
      sectionsPhotos: labels.sectionsPhotos,
    }),
    [labels]
  );

  const handleChange = (nextPhotos) => {
    setPhotos(nextPhotos);
    setDirty(true);
  };

  const savePhotos = async () => {
    setSaving(true);
    onError?.("");
    try {
      const payload = {
        photos: photos
          .map((photo) => photo.url.trim())
          .filter(Boolean)
          .map((url, index) => ({
            url,
            isPrimary: index === 0,
            sortOrder: index,
          })),
      };
      const data = await updateDealerListing(dealerId, listingId, payload);
      trackInventoryEvent("inventory_listing_photos_updated", { listingId, dealerId });
      setDirty(false);
      await onSaved?.(data?.listing?.photos || payload.photos);
    } catch (error) {
      onError?.(error.message || labels.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="dealer-panel dealer-listing-photo-editor">
      <header className="dealer-listing-photo-editor__header">
        <div>
          <h2>{labels.title}</h2>
          <p className="muted">{labels.intro}</p>
        </div>
        <button
          type="button"
          className="dealer-portal-btn primary"
          disabled={disabled || saving || !dirty}
          onClick={savePhotos}
        >
          {saving ? labels.saving : labels.save}
        </button>
      </header>
      <DealerListingPhotoManager
        photos={photos}
        disabled={disabled || saving}
        labels={photoLabels}
        onChange={handleChange}
      />
    </section>
  );
}
